import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { ChevronDown, Pencil, Pin, PinOff, Trash2 } from 'lucide-react'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import {
  BusinessMemoryItemFieldsFragment,
  useDeleteBusinessMemoryMutation,
  useUpdateBusinessMemoryMutation,
} from '__generated__/api-types-and-hooks'
import { formatLocalizedDate } from 'i18n/formatDate'
import { errorTypeOf, explainedMessage } from 'components/Common/AppCoachMemory/coachMemoryErrors'

// Same limit as the API (one plain sentence)
export const MAX_MEMORY_TEXT_LENGTH = 280

// Kinds that can have a due date
const DUE_KINDS = ['commitment', 'open_loop']

const SOURCE_TYPES = [
  'chat_message',
  'comment',
  'task',
  'goal',
  'insight',
  'meeting',
  'onboarding',
  'plan_feedback',
  'explicit',
  'ui_edit',
]

// Authors who were advisors (not part of the business's own team)
const ADVISOR_ROLES = ['advisor', 'tenant_admin']

// The API refuses a change with a message already in the caller's language (a
// conflict, a password in the text, an instruction...). Anything else (a timeout,
// a server fault, a failed request) gets our own wording.
const errorText = (error: unknown, t: TFunction): string =>
  explainedMessage(error) || t('askCL.memory.errors.generic')

// A due date is a calendar day stored as UTC midnight: the same day for everyone,
// wherever they are (read in local time, it would be the day before west of UTC)
const calendarDay = (epochMs: number) => {
  const day = moment.utc(epochMs)
  return moment([day.year(), day.month(), day.date()])
}

interface AppCoachMemoryItemProps {
  item: BusinessMemoryItemFieldsFragment
  // The client's business for advisors; left out for the caller's own business
  businessId?: string
  // Reloads the list (after a change, or when it is out of date)
  onChanged: () => void
  onDeleted: (memoryId: string) => void
  // Someone else deleted the note before a save or pin went through. The card leaves the list
  // with the reload, so the list says why (with the draft that was not saved).
  onGone: (memoryId: string, draft?: string) => void
}

export const AppCoachMemoryItem: React.FC<AppCoachMemoryItemProps> = ({
  item,
  businessId,
  onChanged,
  onDeleted,
  onGone,
}) => {
  const { t, i18n } = useTranslation()
  const updateMutation = useUpdateBusinessMemoryMutation()
  const deleteMutation = useDeleteBusinessMemoryMutation()
  const [mode, setMode] = useState<'view' | 'edit' | 'confirmDelete'>('view')
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showSources, setShowSources] = useState(false)
  // The item as the last change here returned it, until the list catches up
  const [saved, setSaved] = useState<BusinessMemoryItemFieldsFragment | null>(null)
  const current = saved && saved.version > item.version ? saved : item
  // The version the draft started from. If the list comes back with another one,
  // someone changed the note meanwhile: saving would overwrite what they wrote.
  const [draftVersion, setDraftVersion] = useState(item.version)
  const draftIsStale = mode === 'edit' && current.version !== draftVersion

  const editButtonRef = useRef<HTMLButtonElement>(null)
  const deleteButtonRef = useRef<HTMLButtonElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  // The button to focus again when an edit or a delete question closes
  const returnFocusRef = useRef<'edit' | 'delete' | null>(null)
  useEffect(() => {
    if (mode !== 'view' || !returnFocusRef.current) return
    ;(returnFocusRef.current === 'edit' ? editButtonRef : deleteButtonRef).current?.focus()
    returnFocusRef.current = null
  }, [mode])

  const busy = updateMutation.isLoading || deleteMutation.isLoading
  const target = { ...(businessId && { businessId }), memoryId: current.memoryId }
  const idPrefix = `coach-memory-${current.memoryId}`
  const lang = i18n.language

  const trimmed = draft.trim()
  const tooLong = trimmed.length > MAX_MEMORY_TEXT_LENGTH
  const canSave =
    !busy && !draftIsStale && trimmed.length > 0 && !tooLong && trimmed !== current.text

  const startEdit = () => {
    setDraft(current.text)
    setDraftVersion(current.version)
    setError(null)
    setMode('edit')
  }

  const closeEditor = (focus: 'edit' | 'delete') => {
    returnFocusRef.current = focus
    // A refused save is about the draft that is being dropped. A refused delete
    // stays: it closes the question to show why.
    if (focus === 'edit') setError(null)
    setMode('view')
  }

  const save = async () => {
    if (!canSave) return
    setError(null)
    try {
      const result = await updateMutation.mutateAsync({
        input: { ...target, version: draftVersion, text: trimmed },
      })
      setSaved(result.updateBusinessMemory)
      closeEditor('edit')
      onChanged()
    } catch (e) {
      if (errorTypeOf(e) === 'NOT_FOUND') {
        onGone(current.memoryId, trimmed)
        return
      }
      setError(errorText(e, t))
      // Back to the text to fix it (Save lost focus while it was disabled)
      textareaRef.current?.focus()
      // A conflict shows up as a newer version in the reloaded list (see draftIsStale)
      onChanged()
    }
  }

  const togglePin = async () => {
    // aria-disabled, not disabled, while busy: a disabled button would drop the focus
    if (busy) return
    setError(null)
    try {
      const result = await updateMutation.mutateAsync({
        input: { ...target, version: current.version, pinned: !current.pinned },
      })
      setSaved(result.updateBusinessMemory)
      onChanged()
    } catch (e) {
      if (errorTypeOf(e) === 'NOT_FOUND') {
        onGone(current.memoryId)
        return
      }
      setError(errorText(e, t))
      // Shows the latest version, so trying again works after a conflict
      onChanged()
    }
  }

  const remove = async () => {
    setError(null)
    try {
      await deleteMutation.mutateAsync({ input: { ...target, version: current.version } })
      onDeleted(current.memoryId)
      onChanged()
    } catch (e) {
      setError(errorText(e, t))
      closeEditor('delete')
      onChanged()
    }
  }

  // Someone else changed the note: drop the draft and show their version
  const discardStaleDraft = () => {
    setError(null)
    closeEditor('edit')
  }

  const dueDay =
    DUE_KINDS.includes(current.kind) && current.dueAt ? calendarDay(current.dueAt) : null
  const isDue = !!dueDay
  const isOverdue = !!dueDay && dueDay.isBefore(moment(), 'day')
  // The text changed on a later day than it was saved (on the same day, "Saved" says enough).
  // Compared as times, not as formatted dates, so the language's digits never matter.
  const changedLater =
    !!current.lastChangedAt && !moment(current.lastChangedAt).isSame(current.createdAt, 'day')
  const actionClass =
    'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-125 disabled:opacity-50 disabled:cursor-not-allowed'

  return (
    <li className="rounded-lg border border-app-grey-25 bg-white p-3">
      {mode === 'edit' ? (
        <div
          onKeyDown={(e) => {
            // Escape that closes an input method's suggestions (Chinese, Vietnamese...) is
            // the input method's: the editor stays open (closing it would lose the draft),
            // and so does the panel
            if (e.nativeEvent.isComposing || e.keyCode === 229) {
              if (e.key === 'Escape') e.preventDefault()
              return
            }
            // Escape anywhere in the editor (text, Cancel, Save) leaves the editor, not
            // the whole panel: the panel would close and lose the draft
            if (e.key === 'Escape') {
              e.preventDefault()
              if (!busy) closeEditor('edit')
            }
          }}
        >
          <textarea
            ref={textareaRef}
            id={`${idPrefix}-editor`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label={t('askCL.memory.edit.label')}
            aria-invalid={tooLong || trimmed.length === 0}
            aria-describedby={`${idPrefix}-counter`}
            rows={3}
            autoFocus
            // Read-only, not disabled, while saving: a disabled field would drop the focus
            readOnly={busy}
            className="w-full rounded-md border border-app-grey-25 p-2 text-sm text-app-grey-100 focus:outline-none focus:ring-2 focus:ring-app-primary-125"
          />
          <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
            <span
              id={`${idPrefix}-counter`}
              className={`text-xs ${tooLong ? 'text-app-red-60 font-medium' : 'text-app-grey-65'}`}
            >
              {t('askCL.memory.edit.counter', {
                used: trimmed.length,
                max: MAX_MEMORY_TEXT_LENGTH,
              })}
              {tooLong && (
                <span className="block">
                  {t('askCL.memory.edit.tooLong', { max: MAX_MEMORY_TEXT_LENGTH })}
                </span>
              )}
              {trimmed.length === 0 && (
                <span className="block">{t('askCL.memory.edit.empty')}</span>
              )}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => closeEditor('edit')}
                disabled={busy}
                className="rounded-md border border-app-grey-25 px-3 py-1.5 text-xs font-medium text-app-grey-100 hover:bg-app-grey-5 disabled:opacity-50"
              >
                {t('askCL.memory.actions.cancel')}
              </button>
              <button
                type="button"
                onClick={save}
                disabled={!canSave}
                className="rounded-md bg-app-primary-125 px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('askCL.memory.actions.save')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <p
          id={`${idPrefix}-text`}
          className="text-sm text-app-grey-100 whitespace-pre-wrap break-words"
        >
          {current.text}
        </p>
      )}

      {(current.pinned || isDue) && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          {current.pinned && (
            <span className="inline-flex items-center gap-1 rounded-full bg-app-primary-10 px-2 py-0.5 font-medium text-app-primary-125">
              <Pin className="h-3 w-3" aria-hidden="true" />
              {t('askCL.memory.pinned')}
            </span>
          )}
          {dueDay && (
            <span className="text-app-grey-75">
              {t('askCL.memory.due', { date: formatLocalizedDate(dueDay.toDate(), lang) })}
            </span>
          )}
          {isOverdue && (
            <span className="rounded-full bg-app-red-10 px-2 py-0.5 font-medium text-app-red-60">
              {t('askCL.memory.overdue')}
            </span>
          )}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-app-grey-65">
        {current.authorName && (
          <span className="font-medium text-app-grey-75">{current.authorName}</span>
        )}
        {current.authorIsMember === false && (
          <span className="rounded-full bg-app-grey-10 px-2 py-0.5">
            {ADVISOR_ROLES.includes(current.authorRole ?? '')
              ? t('askCL.memory.formerAdvisor')
              : t('askCL.memory.formerMember')}
          </span>
        )}
        <span>
          {t('askCL.memory.savedOn', { date: formatLocalizedDate(current.createdAt, lang) })}
        </span>
        {changedLater && (
          <span>
            {t('askCL.memory.updated', {
              date: formatLocalizedDate(current.lastChangedAt, lang),
            })}
          </span>
        )}
        <span>{t('askCL.memory.timesMentioned', { times: current.sourceCount })}</span>
        {current.userEdited && (
          <span>
            {current.editedByName
              ? t('askCL.memory.editedBy', { name: current.editedByName })
              : t('askCL.memory.edited')}
          </span>
        )}
        {/* Someone else's words replaced part of the note: it keeps its author, but the text
            is not only theirs any more */}
        {!current.userEdited &&
          current.lastChangedByName &&
          current.lastChangedByName !== current.authorName && (
            <span>{t('askCL.memory.changedBy', { name: current.lastChangedByName })}</span>
          )}
      </div>

      {mode === 'confirmDelete' ? (
        <div
          role="group"
          aria-labelledby={`${idPrefix}-confirm`}
          onKeyDown={(e) => {
            // Escape answers "keep it", not "close the panel" (not even while deleting)
            if (e.key === 'Escape') {
              e.preventDefault()
              if (!busy) closeEditor('delete')
            }
          }}
          className="mt-3 rounded-md border border-app-red-20 bg-app-red-10 p-3"
        >
          <p id={`${idPrefix}-confirm`} className="text-sm font-medium text-app-grey-100">
            {t('askCL.memory.deleteConfirm.title')}
          </p>
          <p className="mt-1 text-xs text-app-grey-75">{t('askCL.memory.deleteConfirm.body')}</p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => closeEditor('delete')}
              disabled={busy}
              autoFocus
              className="rounded-md border border-app-grey-25 bg-white px-3 py-1.5 text-xs font-medium text-app-grey-100 hover:bg-app-grey-5 disabled:opacity-50"
            >
              {t('askCL.memory.deleteConfirm.cancel')}
            </button>
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="rounded-md bg-app-red-60 px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {t('askCL.memory.deleteConfirm.confirm')}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {current.sources.length > 0 && (
            <button
              type="button"
              onClick={() => setShowSources((shown) => !shown)}
              aria-expanded={showSources}
              aria-controls={`${idPrefix}-sources`}
              className={`${actionClass} text-app-grey-75 hover:bg-app-grey-5`}
            >
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${showSources ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
              {t('askCL.memory.sources.toggle')}
            </button>
          )}
          {mode === 'view' && current.canEdit && (
            <>
              <button
                ref={editButtonRef}
                type="button"
                onClick={startEdit}
                disabled={busy}
                aria-describedby={`${idPrefix}-text`}
                className={`${actionClass} text-app-grey-75 hover:bg-app-grey-5`}
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                {t('askCL.memory.actions.edit')}
              </button>
              {/* An action named by what it does (Pin / Unpin), so no aria-pressed:
                  "Unpin, pressed" would read as a double negative */}
              <button
                type="button"
                onClick={togglePin}
                aria-disabled={busy || undefined}
                aria-describedby={`${idPrefix}-text`}
                className={`${actionClass} text-app-grey-75 hover:bg-app-grey-5 aria-disabled:opacity-50 aria-disabled:cursor-not-allowed`}
              >
                {current.pinned ? (
                  <PinOff className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <Pin className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {current.pinned ? t('askCL.memory.actions.unpin') : t('askCL.memory.actions.pin')}
              </button>
            </>
          )}
          {mode === 'view' && current.canDelete && (
            <button
              ref={deleteButtonRef}
              type="button"
              onClick={() => {
                setError(null)
                setMode('confirmDelete')
              }}
              disabled={busy}
              aria-describedby={`${idPrefix}-text`}
              className={`${actionClass} text-app-red-60 hover:bg-app-red-10`}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              {t('askCL.memory.actions.delete')}
            </button>
          )}
        </div>
      )}

      {(draftIsStale || error) && (
        <div
          role="alert"
          className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-app-red-10 px-3 py-2 text-xs text-app-red-80"
        >
          <span>{draftIsStale ? t('askCL.memory.errors.conflict') : error}</span>
          {draftIsStale && (
            <button
              type="button"
              onClick={discardStaleDraft}
              className="font-medium underline focus:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-125"
            >
              {t('askCL.memory.actions.reload')}
            </button>
          )}
        </div>
      )}

      {showSources && mode !== 'confirmDelete' && (
        <ul
          id={`${idPrefix}-sources`}
          className="mt-2 list-none space-y-2 border-t border-app-grey-25 pt-2"
        >
          {current.sources.map((source, index) => (
            <li key={`${source.type}-${source.ts}-${index}`} className="text-xs text-app-grey-75">
              <div className="flex flex-wrap gap-x-2">
                <span className="font-medium text-app-grey-100">
                  {t(
                    `askCL.memory.sources.types.${
                      SOURCE_TYPES.includes(source.type) ? source.type : 'other'
                    }`
                  )}
                </span>
                <span>{formatLocalizedDate(source.ts, lang)}</span>
                {source.actorName && <span>{source.actorName}</span>}
              </div>
              {source.quote ? (
                <blockquote className="mt-1 border-s-2 border-app-grey-25 ps-2 italic whitespace-pre-wrap break-words">
                  {source.quote}
                </blockquote>
              ) : source.quoteHidden ? (
                // Someone else's private chat: the words still exist, they are just not shown
                <p className="mt-1 italic text-app-grey-65">{t('askCL.memory.sources.private')}</p>
              ) : (
                // An edit made on this screen has no quote of its own
                source.type !== 'ui_edit' && (
                  <p className="mt-1 italic text-app-grey-65">
                    {t('askCL.memory.sources.removed')}
                  </p>
                )
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}
